// The old games Greg can replay with everyone on a no-game broadcast ("No
// game: just me" → "Watch one of your old games"): fourteen TV broadcasts
// from his career, picked and checked against MLB's box scores in Oct–Nov
// 2026. One of them (sox80-0730) was taped from the bottom of the 6th.
//
// Each viewer's page plays the game in YouTube's own player, in step with his
// console (functions/_lib/booth-room.js `classic`); the footage never goes
// into his stream or his recordings. The timing for each game (where the
// replay starts, commercials to skip, his jump-to moments, the half innings,
// the score, and the live-style pop-ups) is in /classics/<id>.json, made from
// the video's captions and MLB's play-by-play by hand-checked tooling.
//
// `greg` and `why` talk to Greg (his console); `blurb` is for viewers (/live)
// and never gives away how the game ends.
//
// `black` is for an upload with black around its picture: how much of the frame
// it is, in percent, as [top, right, bottom, left], measured in YouTube's player
// at twelve points through the video (Nov 2026). See classicZoom. 1985 ALCS Game 7
// has none: its picture is wider than the box, and filling the box would cut a
// quarter of it off.

export const CLASSICS = [
  {
    id: "ws85-5",
    title: "1985 World Series, Game 5",
    date: "1985-10-24",
    teams: { away: { abbr: "KC", club: "Royals" }, home: { abbr: "STL", club: "Cardinals" } },
    tv: "ABC: Al Michaels, Jim Palmer, Tim McCarver",
    series: "World Series",
    gameNo: 5,
    type: "W",
    yt: "0n4IokC4uZc",
    black: [5.6, 5.6, 5.6, 6.3],
    greg: "You go in at third for George Brett in the 9th, throw out Jack Clark and field Tito Landrum's infield single.",
    why: "The Royals win 6–1 and stay alive.",
    blurb: "Greg goes in at third base for George Brett in the 9th inning.",
  },
  {
    id: "pine-tar",
    title: "The Pine Tar Game",
    date: "1983-07-24",
    teams: { away: { abbr: "KC", club: "Royals" }, home: { abbr: "NYY", club: "Yankees" } },
    tv: "Yankees TV: Frank Messer, Phil Rizzuto, Bill White, Bobby Murcer",
    yt: "Jvu_1JMY36k",
    greg: "July 24: you're in the bullpen with a catcher's mitt, warming up Don Hood, when Brett's homer is taken away. The last 12 minutes are the August 18 finish, with you at third in Brett's place.",
    why: "The most famous game you played in, and the video has your inning.",
    blurb:
      "Greg was in the Royals' bullpen on July 24, and played third base when the game was finished on August 18.",
  },
  {
    id: "disco",
    title: "Disco Demolition Night",
    date: "1979-07-12",
    teams: { away: { abbr: "DET", club: "Tigers" }, home: { abbr: "CWS", club: "White Sox" } },
    tv: "WSNS-TV 44: Harry Caray, Jimmy Piersall",
    yt: "LZaXq338HN4",
    black: [6.3, 7.2, 6.1, 7.3],
    greg: "You start at short and double in the only Sox run (Harry calls it). Then the records blow up and the fans storm the field.",
    why: "You're the only player who played in both Disco Demolition and the Pine Tar Game.",
    blurb: "Greg starts at shortstop in Game 1, before the night everyone remembers.",
  },
  {
    id: "ws85-6",
    title: "1985 World Series, Game 6",
    date: "1985-10-26",
    teams: { away: { abbr: "STL", club: "Cardinals" }, home: { abbr: "KC", club: "Royals" } },
    tv: "ABC: Al Michaels, Jim Palmer, Tim McCarver",
    series: "World Series",
    gameNo: 6,
    type: "W",
    yt: "1Tml85ySvzs",
    greg: "You're in the dugout for Don Denkinger's call and Dane Iorg's single. Jim Sundberg scores the winning run.",
    why: "The most famous game you were in uniform for. (Your book puts the Cardinals' run in the 9th; it came in the 8th.)",
    blurb:
      "Greg was in the Royals' dugout for one of the most famous 9th innings in World Series history.",
  },
  {
    id: "ws85-7",
    title: "1985 World Series, Game 7",
    date: "1985-10-27",
    teams: { away: { abbr: "STL", club: "Cardinals" }, home: { abbr: "KC", club: "Royals" } },
    tv: "ABC: Al Michaels, Jim Palmer, Tim McCarver",
    series: "World Series",
    gameNo: 7,
    type: "W",
    yt: "QqAPmdnzxdE",
    greg: "The ring: Bret Saberhagen's shutout, Darryl Motley's homer, the clubhouse.",
    why: "The night you won the World Series.",
    blurb: "Game 7 of the 1985 World Series, from the Royals' dugout.",
  },
  {
    id: "alcs84-2",
    title: "1984 ALCS, Game 2",
    date: "1984-10-03",
    teams: { away: { abbr: "DET", club: "Tigers" }, home: { abbr: "KC", club: "Royals" } },
    tv: "ABC: Al Michaels, Jim Palmer, Howard Cosell",
    series: "AL Championship Series",
    gameNo: 2,
    type: "L",
    yt: "cK_CG1x-GY8",
    greg: "You pinch-run for Brett in the 10th, stay in at third and take Dan Quisenberry's throw to force Lance Parrish.",
    why: "Your other postseason game, 11 innings, with Howard Cosell on the call.",
    blurb: "Greg pinch-runs for George Brett in extra innings of a playoff game.",
  },
  {
    id: "alcs85-7",
    title: "1985 ALCS, Game 7",
    date: "1985-10-16",
    teams: { away: { abbr: "KC", club: "Royals" }, home: { abbr: "TOR", club: "Blue Jays" } },
    tv: "NBC: Bob Costas, Tony Kubek",
    series: "AL Championship Series",
    gameNo: 7,
    type: "L",
    yt: "SiJAhLw7Wo0",
    greg: "The pennant, and Jim Sundberg's bases-loaded triple. A good night to have Jim back on.",
    why: "Game 7 for the pennant, from the dugout.",
    blurb: "Game 7 for the American League pennant, from the Royals' dugout.",
  },
  {
    id: "munson",
    title: "Thurman Munson's last game",
    date: "1979-08-01",
    teams: { away: { abbr: "NYY", club: "Yankees" }, home: { abbr: "CWS", club: "White Sox" } },
    tv: "WSNS-TV 44: Harry Caray, Jimmy Piersall, Lorn Brown",
    yt: "X7pTeZ50AcA",
    greg: "You start at short and turn two double plays on this copy (it's missing the 5th inning, with your double play on Reggie Jackson). The Yankees' starter is Don Hood, the pitcher you warmed up in the Pine Tar bullpen.",
    why: "Munson died in a plane crash the next day; you were in Yankees camp with him in 1977. Who tagged Bobby Murcer stealing in the 1st? MLB's records say you; Harry says Jorge Orta.",
    blurb: "Greg starts at shortstop in Thurman Munson's last game.",
  },
  {
    id: "sox81-0423",
    title: "Orioles at White Sox, Game 1",
    date: "1981-04-23",
    teams: { away: { abbr: "BAL", club: "Orioles" }, home: { abbr: "CWS", club: "White Sox" } },
    tv: "WGN: Harry Caray, Jimmy Piersall",
    yt: "IzD7K47IWNw",
    black: [6.3, 7.2, 6.3, 6.8],
    greg: "Your best day at the plate on tape: 3-for-5, a double and 3 RBI. The picture is poor.",
    why: "A wild game with lots of runs, and you're in the middle of it.",
    blurb: "Greg at third base, with his best day at the plate on tape.",
  },
  {
    id: "sox80-0518",
    title: "Mariners at White Sox",
    date: "1980-05-18",
    teams: { away: { abbr: "SEA", club: "Mariners" }, home: { abbr: "CWS", club: "White Sox" } },
    tv: "WSNS-TV 44: Harry Caray, Jimmy Piersall",
    yt: "m_ftQWZv7xg",
    black: [0, 1.9, 0, 0.7],
    greg: "Your best day at short on tape: 12 chances, three double plays and an RBI single.",
    why: "A close one, with Harry and Jimmy on the call.",
    blurb: "Greg at shortstop, with Harry Caray and Jimmy Piersall on the call.",
  },
  // The family nights (added Nov 2026): one game each for Michelle and the
  // three girls, picked for them. `guest` is who's in the booth with him.
  {
    id: "sox80-0521",
    title: "Twins at White Sox",
    date: "1980-05-21",
    teams: { away: { abbr: "MIN", club: "Twins" }, home: { abbr: "CWS", club: "White Sox" } },
    tv: "WSNS-TV 44: Harry Caray, Jimmy Piersall",
    yt: "3cD3_luptI0",
    guest: "Michelle",
    greg: "Michelle's night. You start at short, double home a run in the 2nd, single in the 7th and drive in another run in the 9th.",
    why: "Your first season married, and the most you did in any game left on tape.",
    blurb: "Greg at shortstop at Comiskey Park, with his wife, Michelle, in the booth.",
  },
  {
    id: "kc82-0409",
    title: "Tigers at Royals, the 1982 home opener",
    date: "1982-04-09",
    teams: { away: { abbr: "DET", club: "Tigers" }, home: { abbr: "KC", club: "Royals" } },
    tv: "WDIV: George Kell, Al Kaline",
    yt: "t7K7HWWMkPY",
    black: [4.6, 4.5, 4.4, 5],
    guest: "Paige",
    greg: "Paige's night. The Royals' 1982 home opener, 16 days after the trade and two days after she was born. You didn't play; Amos Otis hits a grand slam in the 1st.",
    why: "The week Paige was born, and the Royals' first home game after your trade. She'll ask you where you were.",
    blurb: "The Royals' 1982 home opener, with Greg's oldest daughter, Paige, in the booth.",
  },
  {
    id: "sox80-0730",
    title: "Brewers at White Sox",
    date: "1980-07-30",
    teams: { away: { abbr: "MIL", club: "Brewers" }, home: { abbr: "CWS", club: "White Sox" } },
    tv: "WSNS-TV 44: Harry Caray, Jimmy Piersall",
    yt: "yynWFyGgZ7Y",
    black: [0, 2.8, 0, 0.9],
    guest: "Storey",
    greg: "Storey's night. The tape starts in the bottom of the 6th. You're at third: a single in the 7th, Ricky Seilheimer bunts you to second, and you throw out Gorman Thomas in the 8th.",
    why: "Your only Brewers game on tape, ten days after you called every pitch from shortstop at County Stadium.",
    blurb: "Greg at third base against the Brewers, with his daughter Storey in the booth.",
  },
  {
    id: "kc86-0408",
    title: "Royals at Yankees, Opening Day 1986",
    date: "1986-04-08",
    teams: { away: { abbr: "KC", club: "Royals" }, home: { abbr: "NYY", club: "Yankees" } },
    tv: "WPIX: Phil Rizzuto, Bill White",
    yt: "XRlenTQfKSQ",
    black: [0, 1.6, 1.4, 0.5],
    guest: "Hailey",
    greg: "Hailey's night. Opening Day at Yankee Stadium in your last season. Bud Black starts, and you pinch-hit in the 9th with the bases loaded.",
    why: "Your last Opening Day, the year before Hailey was born, against the Yankees of the book's title.",
    blurb:
      "Opening Day 1986 at Yankee Stadium, with Greg's youngest daughter, Hailey, in the booth.",
  },
];

export const classicById = (id) => CLASSICS.find((c) => c.id === id) || null;

// How much bigger than its box the player is, and the point of the frame at the
// box's centre (percent), so the box cuts off the black around the picture. The zoom
// is set by the direction with more black, which can take up to 2% of the picture off
// the other two edges. TV sets of the time hid that much anyway, so broadcasts kept
// nothing there. null for a tape with no black.
export function classicZoom(game) {
  if (!game?.black) return null;
  const [top, right, bottom, left] = game.black;
  const w = 100 - left - right;
  const h = 100 - top - bottom;
  return { z: 100 / Math.min(w, h), x: left + w / 2, y: top + h / 2 };
}

// "October 24, 1985"
export const classicDate = (iso) =>
  new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });

// The timing for one game (/classics/<id>.json), fetched once per page.
const timings = new Map();
export function loadTiming(id) {
  if (!timings.has(id)) {
    timings.set(
      id,
      fetch(`/classics/${id}.json`)
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`timing ${r.status}`))))
        .catch((err) => {
          timings.delete(id);
          throw err;
        }),
    );
  }
  return timings.get(id);
}

// The video with its skipped stretches (commercials left in the upload) taken
// out: "show time". Everyone's clock runs on show time, so a stretch skipped
// on Greg's console is skipped on every page at the same moment.
export function showTime(skip = [], t) {
  let s = t;
  for (const [a, b] of skip) {
    if (t >= b) s -= b - a;
    else if (t > a) s -= t - a;
  }
  return s;
}
export function videoTime(skip = [], s) {
  let t = s;
  for (const [a, b] of skip) if (t >= a) t += b - a;
  return t;
}
// Inside a skipped stretch: where it ends (else t itself).
export function pastSkip(skip = [], t) {
  for (const [a, b] of skip) if (t >= a && t < b) return b;
  return t;
}

// Where the video should be at `serverNow` (ms, the booth's clock), from the
// room's word { pos, at, playing }. `lag` holds viewers' pages a beat behind
// his console, so his voice (a second or so behind on the stream) lands on
// the play instead of after it.
export function whereNow(classic, skip, serverNow, lag = 0) {
  if (!classic) return 0;
  if (!classic.playing) return classic.pos;
  const s = showTime(skip, classic.pos) + Math.max(0, serverNow - classic.at) / 1000 - lag;
  return videoTime(skip, Math.max(0, s));
}

// The score and half inning at video time t (timing.score: [[t, away, home]],
// timing.halves: [[inning, top (1/0), t]]).
export function scoreAt(timing, t) {
  let a = 0;
  let h = 0;
  for (const [st, sa, sh] of timing?.score || []) {
    if (st > t) break;
    a = sa;
    h = sh;
  }
  let half = null;
  for (const [inn, top, ht] of timing?.halves || []) {
    if (ht > t) break;
    half = { inn, top: !!top };
  }
  return { a, h, half };
}

// The pop-ups that have happened by video time t, for game-popups.jsx.
export const eventsBy = (timing, t) => (timing?.events || []).filter((e) => e.vt <= t);

// 1:08:30
export function clock(sec) {
  const s = Math.max(0, Math.floor(sec || 0));
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, "0");
  return hh ? `${hh}:${String(mm).padStart(2, "0")}:${ss}` : `${mm}:${ss}`;
}
